// File: AdminModels — model manager, car history and staging → prod publish (admin)
// Created: 2026-10-29
// Updated: 2026-10-29
// Author: Erick Hernández Silva

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Boxes, History, Loader2, RotateCcw, Rocket, X } from 'lucide-react';
import type { CarConfig, CarVersionMeta } from './carWizardTypes';
import { fetchCarVersion, fetchCarVersions, publishCarToProd } from '../../services/api.js';

const fmt = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const UNASSIGNED = 'Unassigned';
export const modelNameOf = (c: Pick<CarConfig, 'modelName'>) => c.modelName?.trim() || UNASSIGNED;

/* ==================================================================== *
 * Model manager: cars grouped by model, newest first
 * ==================================================================== */
export const ModelsManager: React.FC<{
  cars: CarConfig[];
  loading: boolean;
  staging: boolean;
  onOpen: (car: CarConfig) => void;
  onClose: () => void;
}> = ({ cars, loading, staging, onOpen, onClose }) => {
  const groups = useMemo(() => {
    const m = new Map<string, CarConfig[]>();
    cars.forEach((c) => { const k = modelNameOf(c); m.set(k, [...(m.get(k) || []), c]); });
    const time = (c: CarConfig) => Date.parse(c.updatedAt || c.createdAt) || 0;
    return [...m.entries()]
      .map(([name, list]) => ({ name, list: list.sort((a, b) => time(b) - time(a)) }))
      .sort((a, b) => (a.name === UNASSIGNED ? 1 : b.name === UNASSIGNED ? -1 : a.name.localeCompare(b.name)));
  }, [cars]);
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => { if (!selected && groups.length) setSelected(groups[0].name); }, [groups, selected]);
  const current = groups.find((g) => g.name === selected);

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="adm-pop bg-white rounded-2xl max-w-5xl w-full h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2"><Boxes size={18} /> Models</h2>
            <p className="text-xs text-slate-500">Cars grouped by model. Set a car&apos;s model in <b>Basic Information → Model</b>.</p>
          </div>
          <button onClick={onClose} className="adm-btn adm-btn-subtle px-3 py-1.5 text-sm">Close</button>
        </div>
        {loading ? (
          <p className="p-6 text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</p>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-[240px_1fr]">
            <div className="border-r border-slate-100 overflow-y-auto p-3 space-y-1">
              {groups.map((g) => (
                <button key={g.name} onClick={() => setSelected(g.name)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${selected === g.name ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'hover:bg-slate-50'}`}>
                  <span className={g.name === UNASSIGNED ? 'italic text-slate-500' : ''}>{g.name}</span>
                  <span className="text-xs text-slate-400">{g.list.length}</span>
                </button>
              ))}
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {current?.list.map((c) => (
                <div key={`${c.id}|${c.createdAt}`} className="adm-card p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.carName || '(unnamed)'}</div>
                    <div className="text-xs text-slate-400 font-mono truncate">{c.id}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Updated {fmt(c.updatedAt || c.createdAt)} · {c.steps?.length || 0} steps · {c.rules?.length || 0} rules
                      {staging && c.lastPublishedAt && <> · <span className="text-emerald-700">published to prod {fmt(c.lastPublishedAt)}</span></>}
                    </div>
                  </div>
                  {c.isPublished && <span className="adm-chip bg-emerald-100 text-emerald-700" title="Shown in the model picker">In picker</span>}
                  <button onClick={() => onOpen(c)} className="adm-btn adm-btn-primary px-3 py-1.5 text-sm">Open</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ==================================================================== *
 * History of the open car
 * ==================================================================== */
const SOURCE_LABEL: Record<string, string> = {
  create: 'Created',
  save: 'Saved',
  publish: 'Published to production',
  'before-publish': 'Production version before a publish',
};

export const HistoryPanel: React.FC<{
  car: CarConfig;
  refreshKey: number; // bump after a save / publish to reload
  onRestore: (snapshot: CarConfig, meta: CarVersionMeta) => void;
}> = ({ car, refreshKey, onRestore }) => {
  const [items, setItems] = useState<CarVersionMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    fetchCarVersions(car.id, car.createdAt)
      .then((list: CarVersionMeta[]) => { if (!cancelled) setItems(list); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [car.id, car.createdAt, refreshKey]);

  const restore = async (meta: CarVersionMeta) => {
    setBusy(meta.versionAt);
    try {
      const { car: snapshot } = await fetchCarVersion(car.id, car.createdAt, meta.versionAt);
      onRestore(snapshot, meta);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-8">
      <p className="text-sm text-slate-500 mb-4 max-w-2xl">
        Every save and every publish keeps a full copy of this car. <b>Load</b> puts that version in the form so you can
        review it in the live preview; nothing changes until you <b>Save</b>.
      </p>
      {error && <div className="bg-rose-50 border border-rose-200 text-rose-900 px-4 py-3 rounded-xl mb-3 text-sm">{error}</div>}
      {!items && !error && <p className="text-slate-500 flex items-center gap-2 text-sm"><Loader2 size={16} className="animate-spin" /> Loading history…</p>}
      {items && items.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-xl py-10 text-center text-slate-400">
          <History size={30} className="mx-auto mb-2 opacity-60" />
          No history yet — it starts with the next save.
        </div>
      )}
      <div className="space-y-2">
        {items?.map((v) => (
          <div key={v.versionAt} className="adm-card p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{fmt(v.versionAt)} · {SOURCE_LABEL[v.source] || v.source}</div>
              <div className="text-xs text-slate-500 truncate">
                {v.carName}{v.note ? <> — <i>{v.note}</i></> : null}
              </div>
            </div>
            <button onClick={() => restore(v)} disabled={!!busy} className="adm-btn adm-btn-subtle px-3 py-1.5 text-sm">
              {busy === v.versionAt ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Load
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================================================================== *
 * Staging → production publish dialog
 * ==================================================================== */
const KEY_STORAGE = 'cfgPublishKey';

export const PublishDialog: React.FC<{
  car: CarConfig;
  dirty: boolean;
  onDone: (message: string) => void;
  onCancel: () => void;
}> = ({ car, dirty, onDone, onCancel }) => {
  const [key, setKey] = useState(() => { try { return sessionStorage.getItem(KEY_STORAGE) || ''; } catch { return ''; } });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ prodUpdatedAt: string; lastPublishedAt: string | null } | null>(null);

  const publish = async (force: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const res = await publishCarToProd(car.id, car.createdAt, { key: key.trim(), note, force });
      if (res.ok) {
        try { sessionStorage.setItem(KEY_STORAGE, key.trim()); } catch {}
        onDone(`Published “${car.carName}” to production (${res.data.images} image(s) copied).`);
        return;
      }
      if (res.status === 409) { setConflict({ prodUpdatedAt: res.data.prodUpdatedAt, lastPublishedAt: res.data.lastPublishedAt }); return; }
      if (res.status === 403) { setError('Wrong publish key.'); return; }
      setError(res.data.error || `Publish failed (HTTP ${res.status}).`);
    } catch (e: any) {
      setError(e.message || 'Publish failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onCancel}>
      <form className="adm-pop bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); if (!busy && key.trim() && !dirty) publish(false); }}>
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-lg font-bold flex items-center gap-2"><Rocket size={18} /> Publish to production</h2>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Copies the <b>saved</b> version of “{car.carName}” and its images to the live site. If production already has
          this car, its current version is kept in the history before being replaced.
        </p>
        {dirty && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-lg mb-3 text-sm flex gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" /> You have unsaved changes. Save first — only the saved version is published.
          </div>
        )}
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">Publish key</label>
        <input type="password" autoFocus value={key} onChange={(e) => setKey(e.target.value)} className="adm-input px-3 py-2 text-sm mb-3" />
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">Note (optional)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed?" className="adm-input px-3 py-2 text-sm" />
        {error && <p className="text-sm text-rose-600 mt-3">{error}</p>}
        {conflict && (
          <div className="bg-rose-50 border border-rose-200 text-rose-900 px-3 py-2 rounded-lg mt-3 text-sm">
            Production was edited on <b>{fmt(conflict.prodUpdatedAt)}</b>
            {conflict.lastPublishedAt ? <>, after the last publish ({fmt(conflict.lastPublishedAt)})</> : <>, after this staging copy</>}.
            Publishing replaces those changes (they stay in the production history).
          </div>
        )}
        <div className="flex justify-end items-center gap-2 mt-5">
          <button type="button" onClick={onCancel} className="adm-btn adm-btn-ghost px-4 py-2 text-sm">Cancel</button>
          {conflict ? (
            <button type="button" disabled={busy} onClick={() => publish(true)} className="adm-btn adm-btn-danger px-4 py-2 text-sm">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} Publish anyway
            </button>
          ) : (
            <button type="submit" disabled={busy || !key.trim() || dirty} className="adm-btn adm-btn-primary px-4 py-2 text-sm">
              {busy ? <><Loader2 size={15} className="animate-spin" /> Publishing…</> : <><Rocket size={15} /> Publish</>}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
